/**
 * Thank You Page
 * Shows appreciation and reward details after customer posts review
 * Public page - no authentication required
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Link,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, Redeem } from '@mui/icons-material';
import { claimReward } from '../services/customerSurveyService';

// Styled Components
const PageContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#F9FAFB',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
});

const ContentCard = styled(Card)({
  maxWidth: '512px',
  width: '100%',
  borderRadius: '16px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
  padding: '24px 32px',
  textAlign: 'center',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E5E7EB',
});

const IconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: '#D1FAE5',
  margin: '0 auto 24px',
});

const RewardCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  borderRadius: '12px',
  backgroundColor: '#EEF2FF',
  padding: '20px',
  border: '1px solid #C7D2FE',
  marginTop: '24px',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const RewardIcon = styled(Box)({
  width: '96px',
  height: '96px',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' rx='20' fill='%234F46E5' opacity='0.1'/%3E%3Cpath d='M100 40 L120 80 L165 87 L132 118 L142 165 L100 143 L58 165 L68 118 L35 87 L80 80 Z' fill='%234F46E5'/%3E%3Ctext x='100' y='120' font-family='Arial' font-size='50' font-weight='bold' text-anchor='middle' fill='%234F46E5'%3E%25%3C/text%3E%3C/svg%3E")`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  flexShrink: 0,
});

interface ThankYouProps {}

const ThankYou: React.FC<ThankYouProps> = () => {
  const location = useLocation();
  
  // Get reward details and IDs from navigation state
  const rewardDetails = location.state?.rewardDetails;
  const campaignId = location.state?.campaignId as string | undefined;
  const uniqueToken = location.state?.uniqueToken as string | undefined;
  const accessHash = location.state?.accessHash as string | undefined;
  const contactInfo = location.state?.contactInfo as { email?: string; phone?: string } | undefined;

  // State - pre-populate with contact info from survey
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [phone, setPhone] = useState(contactInfo?.phone || '');
  const [email, setEmail] = useState(contactInfo?.email || '');
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [claimResult, setClaimResult] = useState<any>(null);

  // Check if rewards exist
  const hasReward = rewardDetails && rewardDetails.reward_type && rewardDetails.reward_value;
  const isCashReward = rewardDetails?.reward_type === 'cash';

  // Debug logging
  React.useEffect(() => {
    console.log('ThankYou page loaded');
    console.log('Reward Details:', rewardDetails);
    console.log('Has Reward:', hasReward);
    console.log('Contact Info:', contactInfo);
    console.log('Campaign ID:', campaignId);
    console.log('Unique Token:', uniqueToken);
    console.log('Access Hash:', accessHash);
  }, [rewardDetails, hasReward, contactInfo, campaignId, uniqueToken, accessHash]);

  const handleOpenDialog = () => {
    console.log('Opening claim reward dialog');
    setIsDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setError(null);
  };

  const handleClaimReward = async () => {
    // Validate inputs
    const phoneValue = phone.trim();
    const emailValue = email.trim();

    // For cash rewards, phone number is ALWAYS mandatory
    if (isCashReward && !phoneValue) {
      setError('Phone number is required for cash rewards');
      return;
    }

    // For non-cash rewards (coupons), either phone OR email is mandatory
    if (!isCashReward && !phoneValue && !emailValue) {
      setError('Please provide either phone number or email address');
      return;
    }

    // Payment method is required for cash rewards
    if (isCashReward && !paymentMethod) {
      setError('Payment method is required for cash rewards');
      return;
    }

    // Validate IDs
    if (!campaignId || !uniqueToken || !accessHash) {
      setError('Missing required information. Please complete the survey first.');
      return;
    }

    try {
      setIsClaiming(true);
      setError(null);

      console.log('🎁 Claiming reward...');

      // Prepare request payload - only include non-empty values
      const requestData: any = {};
      
      if (phoneValue) {
        requestData.phone = phoneValue;
      }
      if (emailValue) {
        requestData.email = emailValue;
      }

      // Add payment method for cash rewards
      if (isCashReward) {
        requestData.payment_method = paymentMethod;
      }

      // Call the API
      const response = await claimReward(
        campaignId,
        uniqueToken,
        accessHash,
        requestData
      );

      if (response.success) {
        console.log('✅ Reward claimed successfully');
        setClaimResult(response.data);
        
        // Show success message
        if (isCashReward) {
          setSuccess(`Cash reward will be sent to ${paymentMethod.toUpperCase()} shortly!`);
        } else {
          setSuccess(`Coupon code: ${response.data?.coupon_code || 'Check your email!'}`);
        }
      } else {
        throw new Error(response.message || 'Failed to claim reward');
      }
    } catch (err) {
      console.error('❌ Error claiming reward:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to claim reward. Please try again.'
      );
    } finally {
      setIsClaiming(false);
    }
  };

  const getRewardMessage = () => {
    if (!hasReward) {
      return 'Thank you for your valuable feedback!';
    }
    if (rewardDetails.reward_type === 'coupon') {
      return `Your ${rewardDetails.reward_value || '15% off'} coupon is ready!`;
    } else if (rewardDetails.reward_type === 'cash') {
      return `Your ${rewardDetails.reward_value || 'cash reward'} is ready!`;
    }
    return 'Your reward is ready!';
  };

  return (
    <PageContainer>
      <Box sx={{ maxWidth: '512px', width: '100%' }}>
        {/* Main Content Card */}
        <ContentCard>
          <CardContent>
            {/* Success Icon */}
            <IconWrapper>
              <CheckCircle sx={{ fontSize: 48, color: '#10B981' }} />
            </IconWrapper>

            {/* Heading */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.875rem', sm: '2.25rem' },
                mb: 2,
                letterSpacing: '-0.033em',
                fontFamily: 'Inter',
                color: '#111827',
              }}
            >
              Thank You!
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: '448px',
                mx: 'auto',
                fontFamily: 'Inter',
                lineHeight: 1.6,
              }}
            >
              Your review has been successfully submitted. We appreciate you taking the time to
              share your feedback.
            </Typography>

            {/* Reward Card - Only show if rewards exist */}
            {hasReward && (
              <>
                <RewardCard>
                  <Box
                    sx={{
                      flex: '2 2 0',
                      textAlign: { xs: 'center', sm: 'left' },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontFamily: 'Inter',
                        color: '#111827',
                      }}
                    >
                      {getRewardMessage()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
                      As a token of our appreciation, here is your reward.
                    </Typography>
                  </Box>
                  <RewardIcon />
                </RewardCard>

                {/* Claim Button */}
                <Box sx={{ mt: 3, px: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleOpenDialog}
                    disabled={!!claimResult}
                    startIcon={<Redeem />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '1rem',
                      maxWidth: '320px',
                      fontFamily: 'Inter',
                      backgroundColor: '#4F46E5',
                      boxShadow: '0px 4px 12px rgba(79, 70, 229, 0.25)',
                      '&:hover': {
                        backgroundColor: '#4338CA',
                      },
                      '&:disabled': {
                        backgroundColor: '#D1D5DB',
                        color: '#9CA3AF',
                      },
                    }}
                  >
                    {claimResult ? 'Reward Claimed' : 'Claim My Reward'}
                  </Button>
                </Box>

                {/* Success Message */}
                {claimResult && (
                  <Alert severity="success" sx={{ mt: 3, borderRadius: '12px', fontFamily: 'Inter' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'Inter' }}>
                      Reward Claimed Successfully!
                    </Typography>
                    {claimResult.coupon_code && (
                      <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Inter' }}>
                        Coupon Code: <strong>{claimResult.coupon_code}</strong>
                      </Typography>
                    )}
                    {claimResult.transaction_id && (
                      <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Inter' }}>
                        Transaction ID: {claimResult.transaction_id}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Inter' }}>
                      {claimResult.message || 'Check your email for details.'}
                    </Typography>
                  </Alert>
                )}
              </>
            )}

            {/* No Reward Message */}
            {!hasReward && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Inter' }}>
                  {getRewardMessage()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </ContentCard>

        {/* Support Link */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            mt: 3,
            px: 2,
            fontFamily: 'Inter',
          }}
        >
          Having trouble?{' '}
          <Link
            href="#"
            sx={{
              color: '#4F46E5',
              textDecoration: 'underline',
              fontWeight: 500,
              fontFamily: 'Inter',
              '&:hover': {
                color: '#4338CA',
              },
            }}
          >
            Contact Support
          </Link>
        </Typography>

        {/* Claim Reward Dialog */}
        <Dialog 
          open={isDialogOpen} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', fontFamily: 'Inter', color: '#111827' }}>
            Claim Your {isCashReward ? 'Cash' : 'Coupon'} Reward
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily: 'Inter' }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: '12px', fontFamily: 'Inter' }}>
                  {success}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Inter' }}>
                {contactInfo?.phone || contactInfo?.email
                  ? 'Your contact details have been pre-filled. Please verify and update if needed.'
                  : isCashReward 
                    ? 'Please provide your phone number (mandatory) and payment method to receive your cash reward. Email is optional.'
                    : 'Please provide your contact details to receive your coupon code.'}
              </Typography>

              <Alert severity="info" sx={{ mb: 3, borderRadius: '12px', fontFamily: 'Inter' }}>
                <Typography variant="body2" sx={{ fontFamily: 'Inter' }}>
                  {isCashReward
                    ? 'Phone number is mandatory for cash rewards. Email is optional.'
                    : 'Please provide either phone number or email address.'}
                </Typography>
              </Alert>

              <TextField
                fullWidth
                required={isCashReward}
                label={isCashReward ? "Phone Number *" : "Phone Number"}
                placeholder="+91-9876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isClaiming || !!success}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontFamily: 'Inter',
                  },
                }}
                helperText={
                  isCashReward
                    ? "Required for cash rewards"
                    : "Provide phone number or email"
                }
                error={isCashReward && !phone.trim() && !!error}
              />

              <TextField
                fullWidth
                label={isCashReward ? "Email Address (Optional)" : "Email Address"}
                type="email"
                placeholder="customer@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isClaiming || !!success}
                sx={{ 
                  mb: isCashReward ? 2 : 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontFamily: 'Inter',
                  },
                }}
                helperText={
                  isCashReward
                    ? "Optional for cash rewards"
                    : "Provide email or phone number"
                }
              />

              {isCashReward && (
                <TextField
                  fullWidth
                  select
                  label="Payment Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={isClaiming || !!success}
                  helperText="Select your preferred payment method"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontFamily: 'Inter',
                    },
                  }}
                >
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="paytm">Paytm</MenuItem>
                  <MenuItem value="gpay">Google Pay</MenuItem>
                  <MenuItem value="phonepe">PhonePe</MenuItem>
                </TextField>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCloseDialog}
              disabled={isClaiming}
              sx={{ 
                textTransform: 'none',
                fontFamily: 'Inter',
                fontWeight: 500,
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              {success ? 'Close' : 'Cancel'}
            </Button>
            {!success && (
              <Button 
                onClick={handleClaimReward}
                variant="contained"
                disabled={isClaiming}
                sx={{
                  textTransform: 'none',
                  minWidth: '120px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  borderRadius: '8px',
                  backgroundColor: '#4F46E5',
                  '&:hover': {
                    backgroundColor: '#4338CA',
                  },
                  '&:disabled': {
                    backgroundColor: '#D1D5DB',
                    color: '#9CA3AF',
                  },
                }}
              >
                {isClaiming ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                    Claiming...
                  </>
                ) : (
                  'Claim Reward'
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </PageContainer>
  );
};

export default ThankYou;

